import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SelectTreeProps} from 'ui-library/select-tree/SelectTree';
import {ElementMouseEvent} from 'util/Types';

export interface SelectTreeListItemProps {
  disabled?: boolean;
  id?: string;
  children?: React.ReactNode;
  icon?: BenchlingIconName;
  /** true: open, false: closed, null: not expandable */
  isExpanded?: boolean | null;
  isSelected: boolean;
  isSelectedDirectly: boolean;
  label: JSX.Element;
  ariaLabel: string;
  onClick: (e: ElementMouseEvent) => void;
  onRequestToggleExpand: () => void;
  tooltipText?: string | null;
  secondaryLabel?: JSX.Element | null;
  labels?: Array<JSX.Element> | null;
  level?: number;
  columnWidths?: SelectTreeProps['columnWidths'];
  className?: string;
  selectedClassName?: string;
  onHover?: (id: string, isHovering: boolean) => void;
}

/**
 * An <li> to be rendered inside a SelectTree that supports:
 *  - an optional item icon
 *  - selected state (green check)
 *  - indirectly selected state (faded check)
 *  - expanded state (none for leaves, caret right or down if expanded or not)
 *  - handles for selecting / toggling expanded state.
 */
export default class SelectTreeListItem extends React.Component<SelectTreeListItemProps> {
  static defaultProps = {
    isSelected: false,
    isSelectedDirectly: false,
  };

  private handleIconClick = (e: ElementMouseEvent): void => {
    if (this.props.isExpanded != null) {
      e.stopPropagation(); // Avoid triggering onClick
      this.props.onRequestToggleExpand();
    }
  };

  private handleIconDoubleClick = (e: ElementMouseEvent): void => {
    e.stopPropagation(); // Avoid triggering handleDoubleClick
  };

  private handleDoubleClick = (e: ElementMouseEvent): void => {
    if (this.props.isExpanded != null) {
      this.props.onRequestToggleExpand();
    }
  };

  private handleHover = (id: string | undefined, isHovering: boolean): void => {
    if (id == null) {
      return;
    }
    this.props.onHover?.(id, isHovering);
  };

  private getCaretIcon = (isExpanded: boolean): BenchlingIconName =>
    isExpanded ? 'caret-down' : 'caret-right';

  override render(): JSX.Element {
    const spacingStyle = StyleSheet.create({
      spacer: {
        width: `${20 * (this.props.level || 0)}px`,
        display: 'inline-block',
      },
    });

    const {labels, columnWidths} = this.props;
    if (labels && labels.length > 0 && columnWidths && columnWidths.length > 0) {
      assertAndContinue(
        labels.length === columnWidths.length - 1,
        'Mismatched number of labels and column widths'
      );
    }

    return (
      <li
        {...getTestProps({component: 'SelectTreeListItem', element: 'root'})}
        aria-label={this.props.ariaLabel}
      >
        <Tooltip
          text={this.props.tooltipText}
          placement={FloatingPlacement.TOP}
          disableContentSelection={true}
          toggleDisplayMode={ToggleDisplayMode.BLOCK}
        >
          <>
            <div
              className={classNames(
                'selectTree-row',
                {'selectTree-row--disabled': this.props.disabled},
                this.props.className,
                this.props.isSelected && this.props.selectedClassName
              )}
              onClick={this.props.disabled ? undefined : this.props.onClick}
              onDoubleClick={this.props.disabled ? undefined : this.handleDoubleClick}
              data-node-id={this.props.id}
              onMouseEnter={() => {
                this.handleHover(this.props.id, true);
              }}
              onMouseLeave={() => {
                this.handleHover(this.props.id, false);
              }}
              {...getTestProps({component: 'SelectTreeListItem', element: 'selectTreeRow'})}
            >
              <div
                className="selectTree-column"
                style={this.props.columnWidths != null ? {width: this.props.columnWidths[0]} : {}}
              >
                <span className={css(spacingStyle.spacer)} />
                {this.props.isExpanded != null ? (
                  <BenchlingIcon
                    className="selectTree-expandIcon"
                    icon={this.getCaretIcon(this.props.isExpanded)}
                    onClick={this.handleIconClick}
                    onDoubleClick={this.props.disabled ? undefined : this.handleIconDoubleClick}
                    dataTestAttrs={{
                      component: 'SelectTreeListItem',
                      element: this.getCaretIcon(this.props.isExpanded),
                    }}
                  />
                ) : (
                  <span className="selectTree-fixedWidthSpacer" />
                )}
                <span className={this.props.disabled ? 'selectTree--disabled' : 'u-pointer'}>
                  {this.props.icon != null && (
                    <BenchlingIcon
                      className={classNames('selectTree-folderIcon u-tiny-spacing-right', {
                        'u-success-color': this.props.isSelectedDirectly,
                        faded: this.props.disabled,
                      })}
                      icon={this.props.isSelected ? 'check' : this.props.icon}
                      onDoubleClick={this.props.disabled ? undefined : this.handleIconDoubleClick}
                    />
                  )}
                  <span
                    className={classNames({
                      faded: this.props.isSelected && !this.props.isSelectedDirectly,
                    })}
                    onDoubleClick={this.props.disabled ? undefined : this.handleIconDoubleClick}
                    data-test-element="primaryLabel"
                  >
                    {this.props.label}
                  </span>
                </span>
              </div>
              {this.props.labels && this.props.labels?.length > 0 ? (
                this.props.labels?.map((label, index) => (
                  <div
                    key={index}
                    className="selectTree-secondaryLabel selectTree-column"
                    style={this.props.columnWidths != null ? {width: this.props.columnWidths[index + 1]} : {}}
                  >
                    {label}
                  </div>
                ))
              ) : (
                <div className="selectTree-secondaryLabel">{this.props.secondaryLabel}</div>
              )}
            </div>
            {this.props.children}
          </>
        </Tooltip>
      </li>
    );
  }
}
